/**
 * MonteCarloPanel — minimal results browser for Monte Carlo simulations.
 * Phase 4.10 β6 — WQ-241.
 *
 * Reads /api/admin/ai/analytics/simulations and lists the most recent runs
 * with status badge, trial count, and creation timestamp. Polls every 30s
 * to surface status transitions (pending → running → completed/failed).
 *
 * The MonteCarloWizard is rendered above this panel by BICommandCenterPage
 * (operator launch path); this panel is read-only and provides the host
 * surface for the new Monte Carlo tab.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { listSimulations, type SimulationOut, type SimulationStatus } from '../../services/monteCarloService';

const STATUS_COLORS: Record<SimulationStatus, { bg: string; fg: string }> = {
  pending: { bg: '#fef3c7', fg: '#92400e' },
  running: { bg: '#dbeafe', fg: '#1e3a8a' },
  completed: { bg: '#d1fae5', fg: '#065f46' },
  failed: { bg: '#fee2e2', fg: '#991b1b' },
};

const POLL_INTERVAL_MS = 30000;

const MonteCarloPanel: React.FC = () => {
  const { authFetch } = useAuth();
  const [simulations, setSimulations] = useState<SimulationOut[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<SimulationStatus | ''>('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSimulations(authFetch, {
        status: statusFilter || undefined,
        limit: 50,
      });
      setSimulations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load simulations');
    } finally {
      setLoading(false);
    }
  }, [authFetch, statusFilter]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  const counts = useMemo(() => {
    const result: Record<string, number> = { total: simulations.length, pending: 0, running: 0, completed: 0, failed: 0 };
    for (const s of simulations) result[s.status] = (result[s.status] ?? 0) + 1;
    return result;
  }, [simulations]);

  return (
    <div data-testid="mc-panel" style={{ padding: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Monte Carlo Simulations</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {counts.total} total · {counts.completed} completed · {counts.running} running · {counts.pending} pending · {counts.failed} failed
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            data-testid="mc-panel-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as SimulationStatus | '')}
            style={{
              padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)',
              background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 13,
            }}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <button
            data-testid="mc-panel-refresh"
            onClick={fetchData}
            disabled={loading}
            style={{
              padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
              background: 'var(--bg-secondary)', color: 'var(--text-primary)',
              fontSize: 13, cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div data-testid="mc-panel-error" style={{
          padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 12,
        }}>
          {error}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && simulations.length === 0 && (
        <div data-testid="mc-panel-empty" style={{
          padding: 24, textAlign: 'center', color: 'var(--text-secondary)',
          border: '1px dashed var(--border)', borderRadius: 8,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No simulations yet</div>
          <div style={{ fontSize: 13 }}>
            Click <strong>+ Run Simulation</strong> above to launch your first Monte Carlo run.
          </div>
        </div>
      )}

      {/* Table */}
      {simulations.length > 0 && (
        <table data-testid="mc-panel-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px', fontWeight: 600 }}>Name</th>
              <th style={{ padding: '8px 4px', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'right' }}>Trials</th>
              <th style={{ padding: '8px 4px', fontWeight: 600 }}>Created</th>
              <th style={{ padding: '8px 4px', fontWeight: 600 }}>Completed</th>
            </tr>
          </thead>
          <tbody>
            {simulations.map((s) => {
              const colors = STATUS_COLORS[s.status] ?? STATUS_COLORS.pending;
              return (
                <tr key={s.id} data-testid={`mc-panel-row-${s.id}`} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 4px' }}>
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                    {s.description && (
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.description}</div>
                    )}
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <span
                      data-testid={`mc-panel-row-${s.id}-status`}
                      style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11,
                        background: colors.bg, color: colors.fg, fontWeight: 600,
                      }}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td style={{ padding: '8px 4px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {s.num_trials.toLocaleString()}
                  </td>
                  <td style={{ padding: '8px 4px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {s.created_at ? s.created_at.slice(0, 19).replace('T', ' ') : '—'}
                  </td>
                  <td style={{ padding: '8px 4px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {s.completed_at ? s.completed_at.slice(0, 19).replace('T', ' ') : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MonteCarloPanel;
